import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Star,
  MapPin,
  Clock,
  MessageCircle,
  Navigation,
  Heart,
  Share2,
  Store,
  Car,
  ExternalLink,
  Camera,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getLocalCurrentDateTime, useDateHandler } from '../utils/dates';
import { useScrollToTopOnChange } from '../utils/scrollUtils';
import { BusynessTab, ChatTab, CommunityTab, OverviewTab, ParkingTab, TrafficTab } from './LocationModal';
import { eventLogger, getData } from '../utils/auth';

interface Shop {
  id: string;
  name: string;
  centreName?: string;
  category?: string;
  latitude: number;
  longitude: number;
  address?: string;
  phone?: string;
  website?: string;
  openingHours?: string;
  description?: string;
  capacities?: {
    [key: string]: number;
  };
  communityReviews?: Array<{
    id: string;
    userId: string;
    username: string;
    rating: number;
    comment: string;
    timestamp: Date;
  }>;
  parkingOptions?: Array<Map<string, any>>;
}

interface ShopModalProps {
  shop: Shop | null;
  handleClose: () => void;
}

const ShopModal: React.FC<ShopModalProps> = ({ shop, handleClose }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'busyness' | 'community' | 'parking' | 'chat' | 'traffic'>(
    'overview'
  );
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [parkingData, setParkingData] = useState<any>(null);

  const headerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Measure header and tabs height for content positioning
  useEffect(() => {
    const updateHeights = () => {
      if (headerRef.current && tabsRef.current && modalRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const tabsHeight = tabsRef.current.offsetHeight;
        modalRef.current.style.setProperty('--modal-header-height', `${headerHeight}px`);
        modalRef.current.style.setProperty('--modal-tabs-height', `${tabsHeight}px`);
      }
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, [shop?.name]);

  // Use scroll-to-top utility hook with automatic scrolling on tab changes
  const { scrollToTop } = useScrollToTopOnChange([activeTab], '.shop-modal .modal-content');
  const {
    convertedCurrentDateTime,
    setConvertedCurrentDateTime,
    selectedDateTimes,
    setSelectedDateTimes,
    setUpDate,
    setUpAllDates,
    getDayCapacities,
    getCurrentCapacity,
  } = useDateHandler();

  useEffect(() => {
    const currentDateTime = getLocalCurrentDateTime(shop.latitude, shop.longitude);
    setConvertedCurrentDateTime(setUpDate(currentDateTime.dateTime));
    setUpAllDates(shop.capacities);
    setSelectedDay(currentDateTime.day);
  }, []);

  useEffect(() => {
    setSelectedDateTimes(getDayCapacities(selectedDay));
  }, [selectedDay]);

  // handles API calls if specific tabs are selected
  useEffect(() => {
    eventLogger('location_tab', { location: `${shop.name} (${shop.centreName})`, tab: activeTab });
    if (activeTab === 'parking' && !parkingData) {
      console.log(`🚗 Fetching parking data for location: ${shop.name} (${shop.centreName})`);
      getData(`/storeparkinginfo/${shop.name} (${shop.centreName})`)
        .then((data) => setParkingData(data))
        .catch(() => console.log('No parking data found'));
    }
  }, [activeTab]);

  const getWaitTime = (capacity: number) => {
    if (capacity < 30) return '< 2 mins';
    if (capacity <= 60) return '2 - 5 mins';
    if (capacity <= 80) return '5 - 10 mins';
    return '10 - 15 mins';
  };

  const getBestTimeToVisit = (capacities: any, currentDay: string) => {
    if (!capacities || Object.keys(capacities).length === 0) return 'Check crowds tab';

    const dayCapacities = getDayCapacities(currentDay);

    if (dayCapacities.length === 0) return 'Check crowds tab';

    const lowestCapacity = dayCapacities.reduce((min, [_, capacity]) => Math.min(min, Number(capacity)), 100);

    if (lowestCapacity < 30) return 'Very quiet - great time!';
    if (lowestCapacity < 50) return 'Low crowds - good time';
    if (lowestCapacity < 70) return 'Moderate crowds';
    return 'Usually busy - check crowds tab';
  };

  const waitTime = getWaitTime(getCurrentCapacity());
  const bestTimeToVisit = getBestTimeToVisit(shop.capacities, selectedDay);

  const handleSaveShop = () => {
    // TODO: Implement save functionality
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
        title: `Check out ${shop.name} on VibeCheck!`,
        text: `I found this great shop with ${getCurrentCapacity()}% crowd level!`,
        url: window.location.href,
        })
        .catch((error) => {
        // Silently handle share cancellation or errors
        if (error.name !== 'AbortError') {
          console.log('Share error:', error);
        }
      });
    }
  };

  const renderStars = (rating: number) => {
    return Array.from(
      {
        length: 5,
      },
      (_, i) => (
        <Star
          key={i}
          size={16}
          className={i < Math.floor(rating) ? 'star-filled' : 'star-empty'}
          fill={i < Math.floor(rating) ? '#FFD700' : 'none'}
        />
      )
    );
  };

  const getBusynessColor = (busyness: number): string => {
    if (busyness < 30) return '#4CAF50'; // Green - Low busyness
    if (busyness < 60) return '#FF9800'; // Orange - Medium busyness
    if (busyness < 80) return '#FF5722'; // Red-Orange - High busyness
    return '#F44336'; // Red - Very high busyness
  };

  // Create shop data for enhanced features
  const shopData = {
    _id: shop.id,
    name: shop.name,
    location: shop.name,
    latitude: shop.latitude,
    longitude: shop.longitude,
    category: (shop.category as any) || 'Shopping', // Cast to LocationCategory
    currentCapacity: getCurrentCapacity(),
    vibeRating: 4.2, // Default rating, could be calculated from reviews
    communityRecommendations: (shop.communityReviews || [])
      .map((review: any) => ({
        id: review.id || '1',
        userId: review.userId || 'user1',
        username: review.username || 'LocalShopper',
        bestTimeToVisit: review.bestTimeToVisit || 'Early morning (9-10 AM)',
        parkingSuggestion: review.parkingSuggestion || 'Street parking nearby',
        rating: review.rating || 5,
        comment: review.comment || 'Great selection and friendly staff!',
        timestamp: review.timestamp || new Date(),
      }))
      .concat([
        {
          id: '1',
          userId: 'user1',
          username: 'LocalShopper',
          bestTimeToVisit: 'Early morning (9-10 AM)',
          parkingSuggestion: 'Street parking nearby',
          rating: 5,
          comment: 'Great selection and friendly staff!',
          timestamp: new Date(),
        },
      ]),
    parkingOptions: shop.parkingOptions || [
      {
        id: '1',
        latitude: shop.latitude,
        longitude: shop.longitude + 0.001,
        description: 'Street parking nearby',
        rating: 4.5,
        totalRatings: 23,
        suggestedBy: 'Community',
        isOfficial: false,
      },
    ],
    capacities: shop.capacities || {},
    shops: {},
    // Add details object for TrafficTab compatibility
    details: {
      latitude: shop.latitude,
      longitude: shop.longitude,
      name: shop.name,
    },
  };

  return (
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      className="modal-overlay fullscreen"
      onClick={handleClose}
    >
      <motion.div
        initial={{
          scale: 0.95,
          opacity: 0,
        }}
        animate={{
          scale: 1,
          opacity: 1,
        }}
        exit={{
          scale: 0.95,
          opacity: 0,
        }}
        transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300,
        }}
        ref={modalRef}
        className="location-modal fullscreen shop-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div ref={headerRef} className="modal-header fullscreen">
          <div className="header-left">
            <h1 className="location-title">
              {shop.name} - {shop.address?.split(', ')[1] || 'Location'}
            </h1>
          </div>
          <div className="header-right">
            {/* Favorites button hidden for now */}
            {/* <button onClick={handleSaveShop} className="icon-action-button" title="Save">
              <Heart size={18} />
            </button> */}
            <button onClick={handleShare} className="icon-action-button" title="Share">
              <Share2 size={18} />
            </button>
            <button onClick={handleClose} className="icon-action-button close" title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div ref={tabsRef} className="modal-tabs">
          {[
            {
              id: 'overview',
              label: 'Overview',
              icon: MapPin,
            },
            {
              id: 'busyness',
              label: 'Crowds',
              icon: Clock,
            },
            {
              id: 'parking',
              label: 'Parking',
              icon: Car,
            },
            {
              id: 'traffic',
              label: 'Traffic',
              icon: Camera,
            },
            {
              id: 'community',
              label: 'Community',
              icon: MessageCircle,
            },
            {
              id: 'chat',
              label: 'AI Chat',
              icon: MessageCircle,
            },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-tab={id}
              onClick={() => {
                setActiveTab(id as any);
              }}
              className={`tab-button ${activeTab === id ? 'active' : ''}`}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div className="modal-content fullscreen">
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <OverviewTab
                getCurrentCapacity={getCurrentCapacity}
                parkTime={waitTime}
                bestTimeToVisit={bestTimeToVisit}
                getBusynessColor={getBusynessColor}
                locationDetails={shopData}
                convertedCurrentDateTime={convertedCurrentDateTime}
                isShop={true}
                getDayCapacities={getDayCapacities}
                onPlanVisit={() => {
                  // Navigate to ShopModal's busyness tab and planner sub-tab (now second tab)
                  setActiveTab('busyness');
                  // Small delay to ensure tab is active before clicking planner
                  setTimeout(() => {
                    const plannerBtn = document.querySelector(
                      '.shop-modal .busyness-sub-tab:nth-child(2)'
                    ) as HTMLElement;
                    if (plannerBtn) plannerBtn.click();
                  }, 100);
                }}
                onViewSchedule={() => {
                  // Navigate to ShopModal's busyness tab and Crowd Analyser sub-tab (week tab, now first)
                  setActiveTab('busyness');
                  // Small delay to ensure tab is active before clicking Crowd Analyser
                  setTimeout(() => {
                    const crowdAnalyserBtn = document.querySelector('.shop-modal .busyness-sub-tab:nth-child(1)') as HTMLElement;
                    if (crowdAnalyserBtn) crowdAnalyserBtn.click();
                  }, 100);
                }}
                onViewWeekSchedule={() => {
                  // Navigate to ShopModal's busyness tab and Crowd Analyser sub-tab (week tab, now first)
                  setActiveTab('busyness');
                  // Small delay to ensure tab is active before clicking Crowd Analyser
                  setTimeout(() => {
                    const crowdAnalyserBtn = document.querySelector('.shop-modal .busyness-sub-tab:nth-child(1)') as HTMLElement;
                    if (crowdAnalyserBtn) crowdAnalyserBtn.click();
                  }, 100);
                }}
                onViewQuieterTimes={() => {
                  // Navigate to ShopModal's busyness tab
                  setActiveTab('busyness');
                }}
              />
            )}

            {activeTab === 'busyness' && (
              <BusynessTab
                convertedCurrentDateTime={convertedCurrentDateTime}
                getCurrentCapacity={getCurrentCapacity}
                getParkTime={getWaitTime}
                getDayCapacities={getDayCapacities}
                capacities={shop.capacities}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
                selectedDateTimes={selectedDateTimes}
                isShop={true}
                name={`${shop.name} (${shop.centreName})`}
              />
            )}

            {activeTab === 'community' && (
              <div className="coming-soon-overlay">
                <div className="coming-soon-content">
                  <div className="coming-soon-icon">🚧</div>
                  <h2>Coming Soon</h2>
                  <p>Community features are currently under development</p>
                </div>
              </div>
            )}

            {activeTab === 'parking' && <ParkingTab details={shopData} parkingData={parkingData} />}

            {activeTab === 'traffic' && <TrafficTab details={shopData} />}

            {activeTab === 'chat' && (
              <div className="coming-soon-overlay">
                <div className="coming-soon-content">
                  <div className="coming-soon-icon">🤖</div>
                  <h2>Coming Soon</h2>
                  <p>AI Chat features are currently under development</p>
                </div>
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default ShopModal;
